import { Button, ButtonProps, SxProps } from '@mui/material';
import React, { CSSProperties, ForwardedRef } from 'react';
import { useStacklineTheme, StacklineTextVariant, StacklineColor, hexToRgba } from '@stackline/ui';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import colors from 'src/utils/colors';

export interface ActiveFilterDotProps {
  width?: string;
  height?: string;
  borderRadius?: string;
  color?: StacklineColor | 'white';
  active?: boolean;
  styles?: CSSProperties;
}
export const ActiveFilterDot = ({
  width = '4px',
  height = '4px',
  borderRadius = '25px',
  color = 'primary',
  active = true,
  styles = {}
}: ActiveFilterDotProps) => {
  const theme = useStacklineTheme();

  return active ? (
    <div
      style={{
        width,
        height,
        border: `1px solid ${theme.colors[color]}`,
        backgroundColor: theme.colors[color],
        borderRadius,
        flexShrink: 0,
        ...styles
      }}
    ></div>
  ) : null;
};

export type SlButtonVariant =
  | 'normal'
  | 'title'
  | 'titleSmall'
  | 'titleExtraSmall'
  | 'contained'
  | 'tertiary'
  | 'accent';

export type SlButtonProps = Omit<ButtonProps, 'variant'> & {
  endIcon?: React.ReactNode;
  startIcon?: React.ReactNode;
  variant?: SlButtonVariant;
  active?: boolean;
  buttonSx?: SxProps;
  textSx?: SxProps;
  iconStyles?: CSSProperties;
};

/**
 * Common button for use through the Stackline website.
 * TODO: Too much going on here, should be broken down into smaller variants for each button
 */
export const SlButton = React.forwardRef(function SlButton(
  {
    children,
    endIcon,
    variant = 'normal',
    active = false,
    buttonSx = {},
    textSx = {},
    startIcon,
    ...rest
  }: SlButtonProps,
  ref: ForwardedRef<HTMLButtonElement>
) {
  const theme = useStacklineTheme();

  const endIconSx: SxProps = {
    padding: '4px 4px 4px 12px'
  };

  const stylesByVariant: { [key in SlButtonVariant]: { default: SxProps; disabled?: SxProps } } = {
    normal: {
      default: {
        border: `solid 1px ${active ? theme.colors.primary : theme.colors.primaryGray}`,
        '&:hover': {
          backgroundColor: theme.colors.secondaryPorcelain
        },
        paddingLeft: active ? '9px' : undefined,
        paddingRight: active ? '6px' : undefined
      },
      disabled: {
        backgroundColor: '#fff',
        border: `solid 1px ${theme.colors.primaryGray}`
      }
    },
    title: {
      default: {
        '&:hover': {
          backgroundColor: 'transparent'
        }
      }
    },
    titleSmall: {
      default: {
        '&:hover': {
          backgroundColor: 'transparent'
        }
      }
    },
    titleExtraSmall: {
      default: {
        '&:hover': {
          backgroundColor: 'transparent'
        }
      }
    },
    contained: {
      default: {
        backgroundColor: theme.colors.primary,
        border: `solid 1px ${theme.colors.primary}`,
        paddingX: '20px',
        '&:hover': {
          backgroundColor: '#68727C',
          borderColor: '#68727C'
        }
      },
      disabled: {
        backgroundColor: '#B3B8BE',
        border: 'solid 1px #B3B8BE'
      }
    },
    tertiary: {
      default: {
        backgroundColor: theme.colors.secondaryPorcelain,
        border: `1px solid ${theme.colors.primaryGray}`,
        '&:hover': {
          backgroundColor: theme.colors.primaryGray
        },
        paddingLeft: '9px',
        paddingRight: '6px'
      },
      disabled: {
        backgroundColor: '#f6f7f8'
      }
    },
    accent: {
      default: {
        backgroundColor: hexToRgba(theme.colors.primaryLight, 0.4),
        '&:hover': {
          backgroundColor: hexToRgba(theme.colors.primaryLight, 0.6)
        },
        paddingLeft: '9px',
        paddingRight: '6px'
      },
      disabled: {
        backgroundColor: 'transparent'
      }
    }
  };

  const textVariantByButtonVariant: { [key in SlButtonVariant]: StacklineTextVariant } = {
    normal: 'subtitle2',
    title: 'h4',
    titleSmall: 'subtitle1',
    titleExtraSmall: 'subtitle2',
    contained: 'subtitle2',
    tertiary: 'subtitle2',
    accent: 'subtitle2'
  };

  const textColorByButtonVariant = {
    normal: rest.disabled ? '#BEC7D3' : colors.darkBlue,
    contained: 'white',
    tertiary: rest.disabled ? '#BEC7D3' : colors.darkBlue,
    accent: rest.disabled ? hexToRgba(colors.darkBlue, 0.15) : colors.darkBlue
  };

  return (
    <Button
      ref={ref}
      disableRipple
      {...rest}
      sx={{
        height: 32,
        textTransform: 'none',
        borderRadius: theme.spacing.xs,
        justifyContent: 'center',
        paddingX: '20px',
        ...(stylesByVariant[variant].default as any),
        ...(rest.disabled ? stylesByVariant[variant]?.disabled : {}),
        ...(endIcon ? endIconSx : {}),
        ...buttonSx
      }}
    >
      {active ? <ActiveFilterDot styles={{ marginRight: '8px' }} /> : null}
      {startIcon || null}
      <Text
        variant={textVariantByButtonVariant[variant]}
        sx={{
          color: textColorByButtonVariant[variant] || colors.darkBlue,
          ...textSx
        }}
      >
        {children}
      </Text>
      {endIcon || null}
    </Button>
  );
});
